import { AdUnitInfo } from "@common";
import {
    CalendarToolForTimeOrganization,
    Luxury,
    ReviewFunnel,
    ReviewLanguage,
    ReviewLocation,
    CrosshairsSolid
} from "@icons";
import {
    adUnitInfoSelector,
    AdUnitInfoWithLinks,
    AppState,
    campaignBudgetForm,
    campaignCreativesForm,
    campaignMediaPlanForm,
    Country,
    CurrencyMapping,
    EstimatedImpressionData,
    existingAdUnitListSelector,
    LanguagesMapping,
    KPIMapping
} from "@store";
import { FetchingState, FETCH_STATE, FETCH_STATE_INITIAL, FETCH_STATE_SUCCESS } from "rd-redux-http";
import * as React from "react";
import { connect } from "react-redux";
import { AccordionCardComponent } from "./AccordionCard";
import { AdUnitInfo as AdUnitInfoPreview } from "./adunit-builder";
import "./CampaignReviewForm.css";
import { EstimatedImpressionsValue } from "./EstimatedImpressionComponent";
import { LongNameComponent } from "./LongNameComponent";
import { ReviewInfoBlock } from "./ReviewInfoBlock";
import { SvgIcon } from "./SvgIcon";
import { TimetableComponent } from "./Timetable";
import { Tour } from "./tour";

function convertDate(date: string): string {
    return date.replace(/-/g, ".");
}

const datesMap = {
    Sun: "Sunday",
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday"
};

export interface CampaignReviewFormComponentProps {
    mediaPlanForm: typeof campaignMediaPlanForm.types.selectorResult;
    creativesForm: typeof campaignCreativesForm.types.selectorResult;
    budgetForm: typeof campaignBudgetForm.types.selectorResult;
    adUnits: AdUnitInfo[];
    timetable: any;
    languagesMapping: LanguagesMapping[];
    kpiMapping: KPIMapping[];
    currencies: CurrencyMapping[];
    countryList: Country[];
    estimatedImpression: EstimatedImpressionData;
    estimatedImpressionFetchState: FETCH_STATE;
    adUnit: AdUnitInfoWithLinks | undefined;
    displayAsSummary: boolean;
}

export const CampaignReviewFormComponent = ({
    mediaPlanForm,
    creativesForm,
    timetable,
    languagesMapping,
    kpiMapping,
    countryList,
    currencies,
    budgetForm,
    estimatedImpression,
    estimatedImpressionFetchState,
    adUnit,
    displayAsSummary
}: CampaignReviewFormComponentProps) => {
    const selectedLanguage =
        languagesMapping && languagesMapping.find(l => l.isoCode === mediaPlanForm.fields.language.value);

    const selectedCountry = countryList && countryList.find(c => c.iso === mediaPlanForm.fields.country.value);

    const selectedRegion =
        selectedCountry && selectedCountry.regions.find(r => r.iso === mediaPlanForm.fields.region.value);

    const selectedTimeValue =
        mediaPlanForm.fields.startDate.value && mediaPlanForm.fields.endDate.value
            ? convertDate(mediaPlanForm.fields.startDate.value) +
              " - " +
              convertDate(mediaPlanForm.fields.endDate.value)
            : undefined;

    const selectedPricingModelValue = mediaPlanForm.fields.priceModel.value
        ? mediaPlanForm.fields.priceModel.value
        : undefined;
    const selectedPrimaryValue = mediaPlanForm.fields.primaryKPI.value ? mediaPlanForm.fields.primaryKPI.value : "None";

    const selectedSecondaryKpi = mediaPlanForm.fields.secondaryKPI.value && mediaPlanForm.fields.secondaryKPI.value;

    const selectedSecondaryValue =
        selectedSecondaryKpi && kpiMapping && kpiMapping.find(k => k.name === selectedSecondaryKpi).display;

    const selectedInterestTargetingValue = mediaPlanForm.fields.interestTargeting.value
        ? mediaPlanForm.fields.interestTargeting.value
        : undefined;

    const selectedAgeValue = mediaPlanForm.fields.age.value ? (
        <ul>{mediaPlanForm.fields.age.value.map((age: any, index: number) => <li key={index}>{age}</li>)}</ul>
    ) : (
        undefined
    );

    const selectedLanguageValue = selectedLanguage ? `Language targeting is ${selectedLanguage.name}` : undefined;

    let selecteLocationValue: string = !selectedCountry && undefined;

    if (selectedCountry) selecteLocationValue = selectedCountry.name;
    if (selectedRegion) selecteLocationValue += `, ${selectedRegion.name}`;
    if (mediaPlanForm.fields.city.value) selecteLocationValue += `, ${mediaPlanForm.fields.city.value}`;
    if (mediaPlanForm.fields.zipCode.value) selecteLocationValue += `, ${mediaPlanForm.fields.zipCode.value}`;

    const selectedTimetableValue =
        timetable && timetable.length
            ? timetable.map((table: any, index: number) => {
                  return (
                      <li key={`timetable-${index}`}>
                          {table.key} -
                          {table.allDay ? (
                              <span className="value_day-value">All day</span>
                          ) : (
                              <span className="value_day-value">
                                  {` ${table.selectedKeys
                                      .map((key: string) => key[0].toUpperCase() + key.slice(1))
                                      .join(", ")}`}
                              </span>
                          )}
                      </li>
                  );
              })
            : undefined;

    const selectedRestrictionsValue =
        mediaPlanForm.fields.restrictions.value && mediaPlanForm.fields.restrictions.value.length ? (
            <ul>
                {mediaPlanForm.fields.restrictions.value.map((restriction: any, index: number) => (
                    <li key={index}>{restriction}</li>
                ))}
            </ul>
        ) : (
            undefined
        );

    const imprassionTracking1Value = creativesForm.fields.impressionTrackingUrl1.value ? (
        <a href={creativesForm.fields.impressionTrackingUrl1.value} target="_blank">
            {creativesForm.fields.impressionTrackingUrl1.value}
        </a>
    ) : (
        undefined
    );

    const imprassionTracking2Value = creativesForm.fields.impressionTrackingUrl2.value ? (
        <a href={creativesForm.fields.impressionTrackingUrl2.value} target="_blank">
            {creativesForm.fields.impressionTrackingUrl2.value}
        </a>
    ) : (
        undefined
    );

    const selectedCurrency = currencies.find(c => c.isoCode === budgetForm.fields.currency.value);

    const budget = selectedCurrency ? (
        <>
            {budgetForm.fields.budget.value}
            <span className="budget-symbol">{selectedCurrency.symbol}</span>
        </>
    ) : (
        budgetForm.fields.budget.value
    );

    const htmlTrackingTags = creativesForm.fields.trackingTags.value ? (
        <blockquote>
            <pre>
                <code>{creativesForm.fields.trackingTags.value}</code>
            </pre>
        </blockquote>
    ) : (
        undefined
    );

    const selectedFormat = mediaPlanForm.fields.adUnitFormat.value;

    return (
        <AccordionCardComponent
            cardHeader={displayAsSummary ? "Review Campaign" : "Order Review"}
            bodyClassName="review tour-campaign-review"
            isOpen={true}
            isSummary={displayAsSummary ? true : false}
        >
            <ul className="review-list">
                <li className="review-list_item">
                    <span className="item_title">Name: </span>
                    <span className="item_value">
                        {mediaPlanForm.fields.campaignName.value ? (
                            <LongNameComponent name={mediaPlanForm.fields.campaignName.value} />
                        ) : (
                            "CAMPAIGN NAME"
                        )}
                    </span>
                </li>
                <li className="review-list_item">
                    <span className="item_title">Dates: </span>
                    <span className="item_value review-dates">
                        <SvgIcon className="badCalendar" svg={CalendarToolForTimeOrganization} /> {selectedTimeValue}
                    </span>
                </li>
                <li className="review-list_item">
                    <span className="item_title">Budget: </span>
                    <span className="item_value">{budget}</span>
                </li>
                <li className="review-list_item">
                    <span className="item_title">Estimated Audience: </span>
                    <span className="item_value">
                        <EstimatedImpressionsValue
                            estimatedImpression={estimatedImpression}
                            estimatedImpressionFetchState={estimatedImpressionFetchState}
                            isReviewComponent={true}
                            isSummaryComponent={displayAsSummary ? true : false}
                        />
                    </span>
                </li>
            </ul>
            <div className="review-wrapper">
                <ReviewInfoBlock title="Media Plan" className="big-block">
                    <ul className="info-list">
                        {selectedPricingModelValue && (
                            <li className="info-list_item">
                                <SvgIcon className="info-list_svg" svg={ReviewFunnel} />
                                <span className="info-list_value">{selectedPricingModelValue}</span>
                            </li>
                        )}
                        {selectedPrimaryValue && (
                            <li className="info-list_item">
                                <SvgIcon className="info-list_svg info-list_svg-crosshair" svg={CrosshairsSolid} />
                                <span className="info-list_value">{selectedPrimaryValue}</span>
                            </li>
                        )}
                        {selectedSecondaryValue && (
                            <li className="info-list_item">
                                <SvgIcon className="info-list_svg info-list_svg-crosshair" svg={CrosshairsSolid} />
                                <span className="info-list_value">{selectedSecondaryValue}</span>
                            </li>
                        )}
                        {selectedInterestTargetingValue && (
                            <li className="info-list_item">
                                <SvgIcon className="info-list_svg" svg={Luxury} />
                                <span className="info-list_value">{selectedInterestTargetingValue}</span>
                            </li>
                        )}
                        {selectedAgeValue && (
                            <li className="info-list_item">
                                <span className="info-list_svg">Age:</span>
                                <span className="info-list_value">{selectedAgeValue}</span>
                            </li>
                        )}
                        {selectedLanguageValue && (
                            <li className="info-list_item">
                                <SvgIcon className="info-list_svg" svg={ReviewLanguage} />
                                <span className="info-list_value">{selectedLanguageValue}</span>
                            </li>
                        )}
                        {selecteLocationValue && (
                            <li className="info-list_item">
                                <SvgIcon className="info-list_svg" svg={ReviewLocation} />
                                <span className="info-list_value">{selecteLocationValue}</span>
                            </li>
                        )}
                    </ul>
                </ReviewInfoBlock>
                <div className="row-block-wrapper">
                    {selectedTimetableValue && (
                        <ReviewInfoBlock title="Times Selected" className="small-block">
                            <TimetableComponent value={mediaPlanForm.fields.timetable.value} isReviewComponent={true} />
                        </ReviewInfoBlock>
                    )}

                    {selectedRestrictionsValue && (
                        <ReviewInfoBlock title="Restrictions" className="small-block">
                            <ul className="info-list">
                                {selectedRestrictionsValue && (
                                    <li className="info-list_item">
                                        <span className="info-list_value">{selectedRestrictionsValue}</span>
                                    </li>
                                )}
                            </ul>
                        </ReviewInfoBlock>
                    )}
                </div>
                <div className="row-block-wrapper" />
                <div className="row-block-wrapper">
                    {adUnit && (
                        <ReviewInfoBlock title="Demo URL" className="small-block">
                            <AdUnitInfoPreview className="preview-component" selectedFormat={selectedFormat} />
                        </ReviewInfoBlock>
                    )}
                    {htmlTrackingTags &&
                        imprassionTracking1Value &&
                        imprassionTracking2Value && (
                            <ReviewInfoBlock title="Tracking" className="small-block">
                                <ul className="info-list">
                                    <li className="info-list_item-tracking">
                                        Creative Link
                                        <span className="info-list_value">
                                            <ul>
                                                {imprassionTracking1Value && <li>{imprassionTracking1Value}</li>}

                                                {imprassionTracking2Value && <li>{imprassionTracking2Value}</li>}
                                            </ul>
                                        </span>
                                    </li>
                                    <li className="info-list_item-code">
                                        HTML Tracking
                                        <span className="info-list_value">{htmlTrackingTags}</span>
                                    </li>
                                </ul>
                            </ReviewInfoBlock>
                        )}
                </div>
            </div>
            <Tour tour="campaign-review" isRunning />
        </AccordionCardComponent>
    );
};
export interface CampaignReviewFormProps {
    countryList: Country[];
}

export const CampaignReviewForm = connect(
    (state: AppState, ownProps: CampaignReviewFormProps): Partial<CampaignReviewFormComponentProps> => {
        const mediaPlanForm = campaignMediaPlanForm.selector(state.campaignState.campaignMediaPlanForm);
        const timetable = getTimetable(mediaPlanForm.fields.timetable.value);

        const languagesMapping: LanguagesMapping[] =
            FetchingState.isSuccess(state.mappings.languages) && state.mappings.languages.data;
        const currencies: CurrencyMapping[] =
            FetchingState.isSuccess(state.mappings.currencies) && state.mappings.currencies.data;
        const kpiMapping: KPIMapping[] = FetchingState.isSuccess(state.mappings.kpi) && state.mappings.kpi.data;

        const adUnit = adUnitInfoSelector(state);

        const displayAsSummary =
            state.campaignState.editingCampaign.fetchState === FETCH_STATE_INITIAL &&
            state.campaignState.summaryCampaign.fetchState === FETCH_STATE_SUCCESS;

        return {
            mediaPlanForm,
            creativesForm: campaignCreativesForm.selector(state.campaignState.campaignCreativesForm),
            budgetForm: campaignBudgetForm.selector(state.campaignState.campaignBudgetForm),
            timetable,
            adUnits: existingAdUnitListSelector(state),
            languagesMapping: languagesMapping || [],
            kpiMapping: kpiMapping || [],
            currencies: currencies || [],
            countryList: ownProps.countryList,
            estimatedImpression:
                state.campaignState.estimatedImpression.fetchState === FETCH_STATE_SUCCESS
                    ? state.campaignState.estimatedImpression.data
                    : {
                          sizing: 0,
                          pricePerUnit: 0,
                          status: undefined
                      },
            estimatedImpressionFetchState: state.campaignState.estimatedImpression.fetchState,
            adUnit,
            displayAsSummary
        };
    }
)(CampaignReviewFormComponent);

function getTimetable(timetableValue: any) {
    if (!timetableValue) {
        return null;
    }

    const newTimetable = Object.keys(timetableValue)
        .map((tableKey: string) => {
            const table = timetableValue[tableKey];
            const tableKeys = Object.keys(table);
            const selectedTableKeys = tableKeys.filter(key => table[key]);
            return {
                key: datesMap[tableKey],
                allDay: selectedTableKeys.length == tableKeys.length,
                selectedKeys: selectedTableKeys
            };
        })
        .filter(table => table.allDay || table.selectedKeys.length);

    return newTimetable;
}
